/**
 * PSP/CRE Tool v2.1 - Helicopter Specifications
 * 
 * Defines performance specifications for medical evacuation helicopters used in
 * NATO and Romanian military operations.
 * 
 * @fileoverview MEDEVAC helicopter specifications and capability modifiers
 * @module constants/helicopterSpecs
 * @author Dr. Tatar Radu MD
 * @date December 22, 2024
 * @version 2.5 - FRENCH nationality fix + EC725 educational note correction (March 15, 2026)
 *   BUG FIX: FRENCH: ['EC725'] was wrong — NH90 Caïman TTH is the primary ALAT MEDEVAC
 *     (63 delivered Feb 2025; replaced Puma fleet). EC725 Caracal is SOF/CSAR only
 *     (4e RHFS), being transferred to Air Force as NH90 SF Std 2 deliveries begin (2025).
 *   FIXED: FRENCH → ['NH90', 'EC725'] — NH90 primary MEDEVAC, EC725 SOF secondary.
 *   BUG FIX: EC725_CARACAL.educational.key_point stated "Primary French ALAT MEDEVAC" —
 *     incorrect. Corrected to reflect SOF/CSAR role and NH90 as primary MEDEVAC.
 *   Sources: DGA NH90 final delivery Feb 4, 2025; defense.gouv.fr 4e RHFS Caracal page;
 *     Airbus Helicopters NH90 delivery announcement Feb 2025.
 * @version 2.4 - National aircraft accuracy + new platforms (March 5, 2026)
 *   NEW: EC725_CARACAL, W3_SOKOL, PUMA_HC2, WILDCAT_AH1 (NOT MEDEVAC), CIVILIAN_HEMS.
 *   HELICOPTER_BY_NATIONALITY corrected: GERMAN=NH90+H145M, added FRENCH/POLISH/BRITISH.
 *   HELICOPTER_BY_ROLE: CASEVAC_SECONDARY, PERMISSIVE_ONLY, NOT_MEDEVAC categories added.
 *   patients_per_sortie + ntm_minutes extended for all new aircraft.
 * @version 2.3 - Phase H: MEDEVAC_OPERATIONAL_FACTORS complete (March 1, 2026)
 * - Added sorties_per_aircraft_per_day by environment and AD-threat key (ARCH-014)
 * - Added ntm_minutes per aircraft from PECC doctrine (IAR330=10, UH60=15, CH47=30)
 * - Added patients_per_sortie planning factors per helicopter code
 * - Added weather_degradation lookup table (parallel to WEATHER_MODIFIERS)
 * - Added ad_threat_modifier flat lookup (direct access for medevacModule)
 * - Fixed calculateDailyMEDEVACCapacity: reads sorties from constant, not hardcoded
 * - Fixed calculateMEDEVACCapacityEnhanced: reads base sorties from constant
 * @version 2.3 - Phase H: MEDEVAC_OPERATIONAL_FACTORS complete (March 1, 2026)
 * - Added evac_planning_mins_per_km and evac_planning_load_minutes to MEDEVAC_OPERATIONAL_FACTORS
 * @version 2.1 - icons.js Integration
 * 
 * Helicopters included:
 * - IAR-330 Puma (Romanian primary MEDEVAC)
 * - UH-60 Black Hawk / HH-60M (US/NATO standard MEDEVAC)
 * - CH-47 Chinook (Heavy lift, mass casualty evacuation)
 * - H145M (European light MEDEVAC)
 * - AW101 Merlin (UK/Italy heavy MEDEVAC)
 * - NH90 (European multinational)
 * - Mi-17 Hip (Legacy, still in NATO partner use)
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (TRANSPORT_ICONS, STATUS_ICONS, WEATHER_ICONS)
 * - Added getHelicopterOptions() with full metadata for UI
 * - Added getDroneThreatOptions(), getADThreatOptions(), getWeatherOptions()
 * - Added validateHelicopterConfig() function
 * - Enhanced educational metadata
 * - Added Ukraine 2022-2024 lessons to each threat level
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

// CALC-010: required-constant reader (throws with the path instead of a literal fallback)
import { requireNumber } from '../calculationEngine/requireValue.js';

import {
  STATUS_ICONS,
  TRANSPORT_ICONS,
  WEATHER_ICONS,
  TACTICAL_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  BLACK: STATUS_ICONS?.BLACK || '⚫',
  
  // Transport icons
  HELICOPTER: TRANSPORT_ICONS?.HELICOPTER || '🚁',
  AIRCRAFT: TRANSPORT_ICONS?.AIRCRAFT || '✈️',
  
  // Weather icons
  SUN: WEATHER_ICONS?.SUN || '☀️',
  CLOUD: WEATHER_ICONS?.CLOUD || '☁️',
  FOG: WEATHER_ICONS?.FOG || '🌫️',
  RAIN: WEATHER_ICONS?.RAIN || '🌧️',
  
  // Tactical icons
  WARNING: TACTICAL_ICONS?.WARNING || '⚠️',
  ALERT: TACTICAL_ICONS?.ALERT || '🚨'
};

// ============================================================================
// DRONE THREAT MODIFIERS (Ukraine 2022-2024 Critical Lessons)
// ============================================================================

/**
 * Drone threat significantly impacts helicopter MEDEVAC operations.
 * Ukraine 2022-2024 data shows helicopters are extremely vulnerable to:
 * - FPV kamikaze drones (low altitude, fast)
 * - Loitering munitions (Lancet, Shahed)
 * - Reconnaissance drones (enable artillery/AD targeting)
 */
export const DRONE_THREAT_MODIFIERS = {
  NONE: {
    code: 'NONE',
    description: 'No drone threat',
    icon: ICONS.GREEN,
    color: '#4CAF50',
    sortie_modifier: 1.0,
    altitude_restriction: null,
    route_modifier: 1.0,
    recommended_tactics: 'Normal operations',
    fpv_risk: 'NONE',
    loitering_munition_risk: 'NONE',
    educational: {
      scenario: 'Peacetime or full air superiority',
      teaching_note: 'Rarely achieved in modern contested environment'
    }
  },
  LOW: {
    code: 'LOW',
    description: 'Occasional reconnaissance drones',
    icon: ICONS.GREEN,
    color: '#8BC34A',
    sortie_modifier: 0.95,
    altitude_restriction: null,
    route_modifier: 1.1,
    recommended_tactics: 'Maintain NOE flight when practical. Vary routes.',
    fpv_risk: 'LOW',
    loitering_munition_risk: 'LOW',
    educational: {
      scenario: 'Rear areas, well-defended airspace',
      teaching_note: 'Still requires vigilance and route variation'
    }
  },
  MEDIUM: {
    code: 'MEDIUM',
    description: 'Regular drone surveillance, occasional FPV attacks',
    icon: ICONS.YELLOW,
    color: '#FFC107',
    sortie_modifier: 0.80,
    altitude_restriction: 'NOE_PREFERRED',
    route_modifier: 1.3,
    recommended_tactics: 'NOE flight mandatory. Use terrain masking. Brief exposure only.',
    fpv_risk: 'MEDIUM',
    loitering_munition_risk: 'LOW',
    educational: {
      scenario: 'Typical Ukraine 2023-2024 conditions',
      teaching_note: '20% sortie reduction expected - plan for it'
    }
  },
  HIGH: {
    code: 'HIGH',
    description: 'Persistent drone threat, frequent FPV/loitering munition attacks',
    icon: ICONS.ORANGE,
    color: '#FF9800',
    sortie_modifier: 0.50,
    altitude_restriction: 'NOE_MANDATORY',
    route_modifier: 1.5,
    recommended_tactics: 'Movement windows only. EW support required. Consider ground evac.',
    fpv_risk: 'HIGH',
    loitering_munition_risk: 'MEDIUM',
    educational: {
      scenario: 'Bakhmut 2023, active contact line',
      teaching_note: 'Ground evacuation may be safer than helicopter',
      ukraine_example: 'Ukrainian forces lost multiple MEDEVAC helicopters to FPV drones'
    }
  },
  EXTREME: {
    code: 'EXTREME',
    description: 'Continuous drone surveillance, immediate attack response',
    icon: ICONS.RED,
    color: '#F44336',
    sortie_modifier: 0.20,
    altitude_restriction: 'NOE_MANDATORY',
    route_modifier: 2.0,
    recommended_tactics: 'Emergency only. Maximum EW support. Ground evacuation preferred.',
    fpv_risk: 'EXTREME',
    loitering_munition_risk: 'HIGH',
    educational: {
      scenario: 'Close combat in urban areas with active drone swarms',
      teaching_note: 'Helicopter MEDEVAC essentially denied - ground only',
      key_point: '80% capacity reduction - almost no helicopter ops possible'
    }
  }
};

// ============================================================================
// AIR DEFENSE THREAT MODIFIERS
// ============================================================================

export const AD_THREAT_MODIFIERS = {
  PERMISSIVE: {
    code: 'PERMISSIVE',
    description: 'No AD threat - air superiority achieved',
    icon: ICONS.GREEN,
    color: '#4CAF50',
    sortie_modifier: 1.0,
    altitude_restriction: null,
    flight_profile: 'NORMAL',
    countermeasures_required: false,
    educational: {
      scenario: 'Full air dominance achieved',
      teaching_note: 'Rare in peer/near-peer conflict'
    }
  },
  LOW: {
    code: 'LOW',
    description: 'MANPADS possible, no radar-guided systems',
    icon: ICONS.YELLOW,
    color: '#CDDC39',
    sortie_modifier: 0.85,
    altitude_restriction: 'AVOID_BELOW_100M_AGL',
    flight_profile: 'TERRAIN_FOLLOWING',
    countermeasures_required: true,
    primary_threat: 'MANPADS',
    threat_systems: ['Igla', 'Stinger', 'Mistral'],
    educational: {
      scenario: 'Degraded enemy AD, insurgent threat',
      teaching_note: 'Countermeasures and terrain masking essential'
    }
  },
  MEDIUM: {
    code: 'MEDIUM',
    description: 'Mobile SAM threat (Strela, Osa)',
    icon: ICONS.ORANGE,
    color: '#FF9800',
    sortie_modifier: 0.60,
    altitude_restriction: 'NOE_PREFERRED',
    flight_profile: 'NOE',
    countermeasures_required: true,
    primary_threat: 'SHORAD',
    threat_systems: ['Strela-10', 'Osa-AKM', 'Tunguska'],
    educational: {
      scenario: 'Ukraine 2022-2024 typical conditions',
      teaching_note: 'NOE flight is survival requirement'
    }
  },
  HIGH: {
    code: 'HIGH',
    description: 'Integrated AD (Buk, S-300)',
    icon: ICONS.RED,
    color: '#F44336',
    sortie_modifier: 0.30,
    altitude_restriction: 'NOE_MANDATORY',
    flight_profile: 'MASKED',
    countermeasures_required: true,
    primary_threat: 'MEDIUM_SAM',
    threat_systems: ['Buk-M1/M2', 'Tor-M1/M2', 'S-300'],
    educational: {
      scenario: 'Near contested FEBA, active SAM coverage',
      teaching_note: '70% sortie reduction - critical planning factor',
      ukraine_example: 'Multiple helicopters lost to Buk systems'
    }
  },
  DENIED: {
    code: 'DENIED',
    description: 'Dense integrated AD - no helicopter operations possible',
    icon: ICONS.BLACK,
    color: '#000000',
    sortie_modifier: 0.0,
    altitude_restriction: 'NO_FLIGHT',
    flight_profile: 'GROUND_ONLY',
    countermeasures_required: true,
    primary_threat: 'LAYERED_IADS',
    threat_systems: ['S-400', 'Pantsir-S1', 'Multiple layers'],
    notes: 'Helicopter MEDEVAC not viable. Use ground evacuation.',
    educational: {
      scenario: 'Deep in enemy territory, overlapping SAM coverage',
      teaching_note: 'Ground evacuation is the ONLY option',
      critical_point: 'Attempting helicopter ops = mission failure'
    }
  }
};

// ============================================================================
// WEATHER MODIFIERS
// ============================================================================

export const WEATHER_MODIFIERS = {
  VMC_CLEAR: {
    code: 'VMC_CLEAR',
    description: 'Visual Meteorological Conditions - Clear',
    icon: ICONS.SUN,
    color: '#4CAF50',
    sortie_modifier: 1.0,
    visibility_km: 10,
    ceiling_m: 3000,
    nvg_effective: true,
    drone_threat_modifier: 1.0
  },
  VMC_MARGINAL: {
    code: 'VMC_MARGINAL',
    description: 'Marginal VMC',
    icon: ICONS.CLOUD,
    color: '#8BC34A',
    sortie_modifier: 0.85,
    visibility_km: 5,
    ceiling_m: 1500,
    nvg_effective: true,
    drone_threat_modifier: 0.8
  },
  IMC_LIGHT: {
    code: 'IMC_LIGHT',
    description: 'Light IMC - IFR capable aircraft only',
    icon: ICONS.CLOUD,
    color: '#FFC107',
    sortie_modifier: 0.60,
    visibility_km: 3,
    ceiling_m: 500,
    nvg_effective: false,
    drone_threat_modifier: 0.5,
    requires: 'IFR_CAPABLE'
  },
  IMC_MODERATE: {
    code: 'IMC_MODERATE',
    description: 'Moderate IMC',
    icon: ICONS.FOG,
    color: '#FF9800',
    sortie_modifier: 0.40,
    visibility_km: 1,
    ceiling_m: 200,
    nvg_effective: false,
    drone_threat_modifier: 0.3,
    requires: 'IFR_CAPABLE'
  },
  IMC_SEVERE: {
    code: 'IMC_SEVERE',
    description: 'Severe IMC - Emergency operations only',
    icon: ICONS.RAIN,
    color: '#F44336',
    sortie_modifier: 0.15,
    visibility_km: 0.5,
    ceiling_m: 100,
    nvg_effective: false,
    drone_threat_modifier: 0.1,
    requires: 'IFR_CAPABLE',
    notes: 'Emergency operations only. High risk.'
  },
  GROUNDED: {
    code: 'GROUNDED',
    description: 'Weather below minimums - No flight',
    icon: ICONS.RED,
    color: '#000000',
    sortie_modifier: 0.0,
    visibility_km: 0,
    ceiling_m: 0,
    nvg_effective: false,
    drone_threat_modifier: 0.0,
    notes: 'Ground evacuation only'
  }
};

// ============================================================================
// TERRAIN MODIFIERS
// ============================================================================

export const TERRAIN_MODIFIERS = {
  FLAT_OPEN: {
    code: 'FLAT_OPEN',
    description: 'Flat, open terrain (plains, desert)',
    icon: '🏜️',
    sortie_modifier: 1.0,
    lz_availability: 'ABUNDANT',
    noe_effectiveness: 'POOR',
    exposure_risk: 'HIGH',
    drone_concealment: 'NONE'
  },
  ROLLING: {
    code: 'ROLLING',
    description: 'Rolling terrain with some cover',
    icon: '🌄',
    sortie_modifier: 0.95,
    lz_availability: 'GOOD',
    noe_effectiveness: 'MODERATE',
    exposure_risk: 'MODERATE',
    drone_concealment: 'LIMITED'
  },
  FORESTED: {
    code: 'FORESTED',
    description: 'Forested terrain',
    icon: '🌲',
    sortie_modifier: 0.85,
    lz_availability: 'LIMITED',
    noe_effectiveness: 'GOOD',
    exposure_risk: 'LOW',
    drone_concealment: 'GOOD'
  },
  MOUNTAINOUS: {
    code: 'MOUNTAINOUS',
    description: 'Mountainous terrain (Carpathians)',
    icon: '⛰️',
    sortie_modifier: 0.75,
    lz_availability: 'LIMITED',
    noe_effectiveness: 'EXCELLENT',
    exposure_risk: 'LOW',
    drone_concealment: 'EXCELLENT',
    requires: 'MOUNTAIN_CAPABLE',
    altitude_penalty: true
  },
  URBAN: {
    code: 'URBAN',
    description: 'Urban terrain',
    icon: '🏙️',
    sortie_modifier: 0.70,
    lz_availability: 'VERY_LIMITED',
    noe_effectiveness: 'POOR',
    exposure_risk: 'HIGH',
    drone_concealment: 'MODERATE',
    special_hazards: ['Wires', 'Buildings', 'Confined LZs']
  },
  COMPLEX: {
    code: 'COMPLEX',
    description: 'Complex terrain (mixed mountains/urban/forest)',
    icon: '🗺️',
    sortie_modifier: 0.65,
    lz_availability: 'LIMITED',
    noe_effectiveness: 'VARIABLE',
    exposure_risk: 'VARIABLE',
    drone_concealment: 'VARIABLE'
  },
  // BUG-T05 fix: MIXED was the default terrain value but missing from TERRAIN_MODIFIERS.
  // Callers in medevacModule.js fell back to ROLLING (0.95) silently.
  // MIXED = varied terrain, moderate LZ availability → sortie_modifier 0.90.
  MIXED: {
    code: 'MIXED',
    description: 'Mixed terrain (varied — baseline planning value)',
    icon: '🗺️',
    sortie_modifier: 0.90,
    lz_availability: 'MODERATE',
    noe_effectiveness: 'MODERATE',
    exposure_risk: 'MODERATE',
    drone_concealment: 'LIMITED'
  }
};

// ============================================================================
// NIGHT OPERATIONS MODIFIERS
// ============================================================================

export const NIGHT_OPERATIONS_MODIFIERS = {
  NVG_EQUIPPED: {
    code: 'NVG_EQUIPPED',
    description: 'Crew NVG qualified, aircraft NVG compatible',
    icon: '🌙',
    sortie_modifier: 0.85,
    speed_reduction: 0.10,
    lz_restriction: 'SURVEYED_OR_MARKED',
    drone_threat_modifier: 0.5,
    ad_threat_modifier: 0.7
  },
  FLIR_EQUIPPED: {
    code: 'FLIR_EQUIPPED',
    description: 'FLIR/thermal equipped',
    icon: '🔭',
    sortie_modifier: 0.90,
    speed_reduction: 0.05,
    lz_restriction: 'MINIMAL',
    drone_threat_modifier: 0.4,
    ad_threat_modifier: 0.6
  },
  NON_NVG: {
    code: 'NON_NVG',
    description: 'No night vision equipment',
    icon: ICONS.RED,
    sortie_modifier: 0.30,
    speed_reduction: 0.40,
    lz_restriction: 'LIT_ONLY',
    drone_threat_modifier: 0.6,
    ad_threat_modifier: 0.8,
    notes: 'Emergency operations only'
  },
  FULL_MOON: {
    code: 'FULL_MOON',
    description: 'High illumination (full moon, clear)',
    icon: '🌕',
    sortie_modifier: 0.70,
    speed_reduction: 0.20,
    lz_restriction: 'SURVEYED',
    drone_threat_modifier: 0.7,
    ad_threat_modifier: 0.85
  }
};

// ============================================================================
// ALTITUDE PERFORMANCE DEGRADATION
// ============================================================================

export const ALTITUDE_PERFORMANCE = {
  SEA_LEVEL: {
    altitude_range: '0-500m',
    icon: ICONS.GREEN,
    power_available: 1.0,
    payload_modifier: 1.0,
    hover_ceiling_modifier: 1.0
  },
  LOW_ALTITUDE: {
    altitude_range: '500-1500m',
    icon: ICONS.GREEN,
    power_available: 0.95,
    payload_modifier: 0.95,
    hover_ceiling_modifier: 0.95
  },
  MEDIUM_ALTITUDE: {
    altitude_range: '1500-2500m',
    icon: ICONS.YELLOW,
    power_available: 0.85,
    payload_modifier: 0.85,
    hover_ceiling_modifier: 0.85,
    notes: 'Carpathian valley operations'
  },
  HIGH_ALTITUDE: {
    altitude_range: '2500-3500m',
    icon: ICONS.ORANGE,
    power_available: 0.70,
    payload_modifier: 0.70,
    hover_ceiling_modifier: 0.70,
    notes: 'Carpathian peak operations - reduced capacity'
  },
  VERY_HIGH_ALTITUDE: {
    altitude_range: '3500-4500m',
    icon: ICONS.RED,
    power_available: 0.55,
    payload_modifier: 0.55,
    hover_ceiling_modifier: 0.55,
    notes: 'Marginal operations - some aircraft only'
  },
  EXTREME_ALTITUDE: {
    altitude_range: '>4500m',
    icon: ICONS.RED,
    power_available: 0.40,
    payload_modifier: 0.40,
    hover_ceiling_modifier: 0.40,
    notes: 'Special high-altitude variant required'
  }
};

// ============================================================================
// COUNTERMEASURES SYSTEMS
// ============================================================================

export const COUNTERMEASURES = {
  NONE: {
    code: 'NONE',
    description: 'No countermeasures',
    icon: ICONS.RED,
    ir_effectiveness: 0.0,
    radar_effectiveness: 0.0,
    manpads_survival_increase: 0.0
  },
  BASIC_FLARES: {
    code: 'BASIC_FLARES',
    description: 'Basic flare dispensers',
    icon: ICONS.YELLOW,
    ir_effectiveness: 0.40,
    radar_effectiveness: 0.0,
    manpads_survival_increase: 0.30,
    flare_count: 30
  },
  ADVANCED_FLARES: {
    code: 'ADVANCED_FLARES',
    description: 'Advanced flare system with MAWS',
    icon: ICONS.YELLOW,
    ir_effectiveness: 0.65,
    radar_effectiveness: 0.0,
    manpads_survival_increase: 0.50,
    flare_count: 60,
    maws: true
  },
  DIRCM: {
    code: 'DIRCM',
    description: 'Directed IR Countermeasures (laser)',
    icon: ICONS.GREEN,
    ir_effectiveness: 0.85,
    radar_effectiveness: 0.0,
    manpads_survival_increase: 0.75,
    active_system: true
  },
  FULL_SUITE: {
    code: 'FULL_SUITE',
    description: 'Full defensive suite (DIRCM + RWR + Chaff)',
    icon: ICONS.GREEN,
    ir_effectiveness: 0.90,
    radar_effectiveness: 0.60,
    manpads_survival_increase: 0.80,
    rwr: true,
    chaff: true,
    active_system: true
  }
};

// ============================================================================
// FIELDING STATUS
// ============================================================================

export const FIELDING_STATUS = {
  ACTIVE: {
    code: 'ACTIVE',
    description: 'Currently in active service',
    icon: ICONS.GREEN,
    availability_modifier: 1.0
  },
  LEGACY: {
    code: 'LEGACY',
    description: 'Being phased out',
    icon: ICONS.ORANGE,
    availability_modifier: 0.8,
    notes: 'Reduced availability, plan for replacement'
  },
  FIELDING: {
    code: 'FIELDING',
    description: 'Currently being fielded',
    icon: ICONS.YELLOW,
    availability_modifier: 0.5
  },
  PLANNED: {
    code: 'PLANNED',
    description: 'Planned acquisition',
    icon: ICONS.RED,
    availability_modifier: 0.0
  }
};

// ============================================================================
// HELICOPTER SPECIFICATIONS
// ============================================================================

/**
 * IAR-330 Puma - Romanian Military Helicopter
 */
export const IAR330_PUMA = {
  code: 'IAR330',
  name: 'IAR-330 Puma',
  nato_designation: 'Puma',
  manufacturer: 'IAR Brașov',
  nationality: 'Romanian',
  alliance: 'NATO',
  flag: '🇷🇴',
  icon: ICONS.HELICOPTER,
  
  capacity: {
    stretchers: 6,
    ambulatory: 12,
    mixed_stretchers: 4,
    mixed_ambulatory: 2,
    crew: 2,
    medics: 1,
    medics_max: 2,
    total_persons: 15
  },
  
  performance: {
    cruise_speed_kmh: 240,
    max_speed_kmh: 260,
    range_km: 550,
    combat_radius_km: 200,
    service_ceiling_m: 4800,
    endurance_hours: 3.5,
    high_altitude_capable: true,
    max_operating_altitude_m: 4500
  },
  
  fuel: {
    capacity_liters: 1540,
    consumption_liters_per_hour: 440,
    consumption_liters_per_km: 1.8,
    refuel_time_minutes: 10
  },
  
  operations: {
    day_operations: true,
    night_operations: true,
    nvg_compatible: true,
    flir_equipped: false,
    all_weather: true,
    ifr_capable: true,
    mountain_operations: true,
    preparation_time_minutes: 15,
    landing_zone_size_m: 30
  },
  
  medical: {
    stretcher_system: 'NATO standard litter',
    oxygen_system: true,
    oxygen_capacity_hours: 6,
    suction_available: true,
    pfc_capable: false,
    blood_refrigeration: false,
    ventilator_support: false,
    suitable_for: [
      'Routine MEDEVAC',
      'Priority MEDEVAC',
      'Urgent MEDEVAC',
      'Mass casualty (6 stretchers)',
      'Mountain rescue operations'
    ],
    not_suitable_for: [
      'Ventilator-dependent patients',
      'Extended duration evacuations (>2h)'
    ]
  },
  
  survivability: {
    armor_protection: 'NONE',
    self_sealing_fuel_tanks: true,
    crashworthy_seats: true,
    countermeasures: 'BASIC_FLARES',
    ircm: false,
    rwr: false,
    maws: false,
    dircm: false,
    ied_survival_rate: 0.60
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'Romanian primary MEDEVAC - 6 stretcher capacity',
    limitation: 'No PFC capability - limit evacuation to 2 hours',
    comparison: 'Similar capability to original UH-60A'
  }
};

/**
 * UH-60 Black Hawk - US/NATO Standard MEDEVAC
 */
export const UH60_BLACK_HAWK = {
  code: 'UH60',
  name: 'UH-60M Black Hawk',
  nato_designation: 'Black Hawk',
  manufacturer: 'Sikorsky',
  nationality: 'US',
  alliance: 'NATO',
  flag: '🇺🇸',
  icon: ICONS.HELICOPTER,
  
  capacity: {
    stretchers: 6,
    ambulatory: 11,
    mixed_stretchers: 4,
    mixed_ambulatory: 3,
    crew: 2,
    medics: 2,
    total_persons: 14
  },
  
  performance: {
    cruise_speed_kmh: 280,
    max_speed_kmh: 295,
    range_km: 580,
    combat_radius_km: 230,
    service_ceiling_m: 5790,
    endurance_hours: 2.3,
    high_altitude_capable: true,
    max_operating_altitude_m: 5000
  },
  
  fuel: {
    capacity_liters: 1360,
    consumption_liters_per_hour: 590,
    consumption_liters_per_km: 2.1,
    refuel_time_minutes: 8
  },
  
  operations: {
    day_operations: true,
    night_operations: true,
    nvg_compatible: true,
    flir_equipped: true,
    all_weather: true,
    ifr_capable: true,
    mountain_operations: true,
    preparation_time_minutes: 10,
    landing_zone_size_m: 25
  },
  
  medical: {
    stretcher_system: 'Modular litter system',
    oxygen_system: true,
    oxygen_capacity_hours: 8,
    suction_available: true,
    pfc_capable: true,
    blood_refrigeration: true,
    ventilator_support: true,
    suitable_for: [
      'All MEDEVAC categories',
      'Prolonged field care',
      'CASEVAC',
      'MASCAL'
    ]
  },
  
  survivability: {
    armor_protection: 'CREW_SEATS',
    self_sealing_fuel_tanks: true,
    crashworthy_seats: true,
    countermeasures: 'ADVANCED_FLARES',
    ircm: true,
    rwr: true,
    maws: true,
    dircm: false,
    ied_survival_rate: 0.75
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'NATO gold standard for MEDEVAC',
    advantage: 'PFC capable - essential for contested environments',
    ukraine_relevance: 'Would significantly improve survival rates'
  }
};

/**
 * CH-47 Chinook - Heavy Lift MEDEVAC
 */
export const CH47_CHINOOK = {
  code: 'CH47',
  name: 'CH-47F Chinook',
  nato_designation: 'Chinook',
  manufacturer: 'Boeing',
  nationality: 'US',
  alliance: 'NATO',
  flag: '🇺🇸',
  icon: ICONS.HELICOPTER,
  
  capacity: {
    stretchers: 24,
    ambulatory: 33,
    mixed_stretchers: 12,
    mixed_ambulatory: 10,
    crew: 3,
    medics: 4,
    total_persons: 55
  },
  
  performance: {
    cruise_speed_kmh: 275,
    max_speed_kmh: 315,
    range_km: 740,
    combat_radius_km: 370,
    service_ceiling_m: 6100,
    endurance_hours: 2.5,
    high_altitude_capable: true
  },
  
  medical: {
    stretcher_system: 'Triple-tier litter system',
    oxygen_system: true,
    pfc_capable: true,
    blood_refrigeration: true,
    suitable_for: [
      'MASCAL evacuation',
      'Hospital evacuation',
      'Strategic MEDEVAC'
    ]
  },
  
  survivability: {
    armor_protection: 'PARTIAL',
    countermeasures: 'ADVANCED_FLARES',
    maws: true,
    dircm: false
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'MASCAL workhorse - 24 stretchers',
    best_use: 'Rearward evacuation, hospital evacuation',
    limitation: 'Large signature, needs secure LZ'
  }
};

/**
 * H145M - European Light MEDEVAC
 */
export const H145M = {
  code: 'H145M',
  name: 'H145M',
  nato_designation: 'H145M',
  manufacturer: 'Airbus Helicopters',
  nationality: 'German',
  alliance: 'NATO',
  flag: '🇩🇪',
  icon: ICONS.HELICOPTER,
  
  capacity: {
    stretchers: 2,
    ambulatory: 6,
    crew: 2,
    medics: 1
  },
  
  performance: {
    cruise_speed_kmh: 240,
    max_speed_kmh: 268,
    range_km: 680,
    combat_radius_km: 270,
    service_ceiling_m: 5485
  },
  
  medical: {
    stretcher_system: 'Integrated EMS',
    pfc_capable: true,
    blood_refrigeration: false
  },
  
  survivability: {
    countermeasures: 'BASIC_FLARES',
    maws: false
  },
  
  fielding_status: 'ACTIVE',
  
  educational: {
    key_point: 'Light, agile - good for urban/confined LZs',
    limitation: 'Only 2 stretchers - tactical use'
  }
};

/**
 * AW101 Merlin - UK/Italy Heavy MEDEVAC
 */
export const AW101_MERLIN = {
  code: 'AW101',
  name: 'AW101 Merlin',
  nato_designation: 'Merlin',
  manufacturer: 'Leonardo/AgustaWestland',
  nationality: 'UK/Italy',
  alliance: 'NATO',
  flag: '🇬🇧',
  icon: ICONS.HELICOPTER,
  
  capacity: {
    stretchers: 16,
    ambulatory: 24,
    crew: 3,
    medics: 3
  },
  
  performance: {
    cruise_speed_kmh: 278,
    max_speed_kmh: 309,
    range_km: 1000,
    combat_radius_km: 400
  },
  
  medical: {
    pfc_capable: true,
    blood_refrigeration: true,
    suitable_for: ['Strategic MEDEVAC', 'MASCAL', 'Naval operations']
  },
  
  survivability: {
    countermeasures: 'DIRCM'
  },
  
  fielding_status: 'ACTIVE'
};

/**
 * NH90 - European Multinational
 */
export const NH90 = {
  code: 'NH90',
  name: 'NH90 TTH',
  nato_designation: 'NH90',
  manufacturer: 'NHIndustries',
  nationality: 'European',
  alliance: 'NATO',
  flag: '🇪🇺',
  icon: ICONS.HELICOPTER,
  
  capacity: {
    stretchers: 12,
    ambulatory: 20,
    crew: 2,
    medics: 2
  },
  
  performance: {
    cruise_speed_kmh: 260,
    max_speed_kmh: 291,
    range_km: 800,
    combat_radius_km: 320
  },
  
  medical: {
    pfc_capable: true,
    blood_refrigeration: true
  },
  
  survivability: {
    countermeasures: 'ADVANCED_FLARES',
    maws: true
  },
  
  fielding_status: 'ACTIVE'
};

/**
 * Mi-17 Hip - Legacy
 */
export const MI17_HIP = {
  code: 'MI17',
  name: 'Mi-17 Hip',
  nato_designation: 'Hip',
  manufacturer: 'Mil Moscow',
  nationality: 'Soviet/Russian',
  alliance: 'LEGACY',
  flag: '🇷🇺',
  icon: ICONS.HELICOPTER,
  
  capacity: {
    stretchers: 12,
    ambulatory: 24,
    crew: 3,
    medics: 2
  },
  
  performance: {
    cruise_speed_kmh: 230,
    max_speed_kmh: 250,
    range_km: 495,
    combat_radius_km: 200
  },
  
  medical: {
    pfc_capable: false,
    blood_refrigeration: false
  },
  
  survivability: {
    countermeasures: 'NONE'
  },
  
  fielding_status: 'LEGACY',
  
  educational: {
    key_point: 'Still in partner nation service',
    limitation: 'No countermeasures - very vulnerable',
    ukraine_lesson: 'High loss rate in contested environment'
  }
};

// ============================================================================
// EC725 / H225M CARACAL - French ALAT + NATO partners MEDEVAC
// ============================================================================

export const EC725_CARACAL = {
  code: 'EC725',
  name: 'EC725 Caracal / H225M',
  nato_designation: 'Caracal',
  manufacturer: 'Airbus Helicopters',
  nationality: 'French/Multinational',
  alliance: 'NATO',
  icon: ICONS.HELICOPTER,

  capacity: {
    stretchers: 6,
    ambulatory: 11,
    crew: 3,
    medics: 2
  },

  performance: {
    cruise_speed_kmh: 260,
    max_speed_kmh: 285,
    range_km: 857,
    combat_radius_km: 350
  },

  medical: {
    pfc_capable: true,
    blood_refrigeration: true,
    intensive_care_capable: true
  },

  survivability: {
    countermeasures: 'ADVANCED',
    armor: 'LIGHT',
    irm: true
  },

  fielding_status: 'CURRENT',

  educational: {
    key_point: 'French ALAT SOF/CSAR platform (4e RHFS, Pau). NOT the primary French line-unit MEDEVAC — NH90 Caïman TTH replaced it as primary ALAT MEDEVAC (63 delivered by Feb 2025). Caracal being transferred to Air Force as 4e RHFS receives NH90 SF Standard 2 (deliveries from mid-2025).',
    teaching_note: 'Long-range MEDEVAC capable — 350 km combat radius. Also used by Poland (H225M) and Hungary (H225M) for CSAR/MEDEVAC.',
    doctrinal_note: 'French TTA 400. Assigned exclusively to 4e Régiment d\'Hélicoptères des Forces Spéciales (4e RHFS). Line units request MEDEVAC via standard NATO 9-line; fulfilled by NH90 Caïman (ALAT) not Caracal. Source: defense.gouv.fr, DGA NH90 final delivery Feb 4, 2025.'
  }
};

// ============================================================================
// W-3 SOKOL - Polish Land Forces primary MEDEVAC
// ============================================================================

export const W3_SOKOL = {
  code: 'W3_SOKOL',
  name: 'W-3 Sokol',
  nato_designation: 'Sokol',
  manufacturer: 'PZL Swidnik',
  nationality: 'Polish',
  alliance: 'NATO',
  icon: ICONS.HELICOPTER,

  capacity: {
    stretchers: 4,
    ambulatory: 8,
    crew: 2,
    medics: 1
  },

  performance: {
    cruise_speed_kmh: 210,
    max_speed_kmh: 255,
    range_km: 740,
    combat_radius_km: 280
  },

  medical: {
    pfc_capable: false,
    blood_refrigeration: false
  },

  survivability: {
    countermeasures: 'BASIC'
  },

  fielding_status: 'CURRENT',

  educational: {
    key_point: 'Primary Polish Land Forces MEDEVAC — domestically produced at PZL Swidnik',
    teaching_note: 'Poland also transitioning to H225M Caracal for CSAR/MEDEVAC role',
    doctrinal_note: 'Polish LWL (Wojska Ladowe) aviation doctrine. W-3 is legacy platform; H225M is new.'
  }
};

// ============================================================================
// PUMA HC2 - RAF Support Helicopter Force (SHF) MEDEVAC/CASEVAC
// Note: RAF SHF platform, NOT IAR-330 Puma. Distinct from Romanian IAR-330.
// ============================================================================

export const PUMA_HC2 = {
  code: 'PUMA_HC2',
  name: 'Puma HC2',
  nato_designation: 'Puma',
  manufacturer: 'Airbus Helicopters (Westland)',
  nationality: 'British',
  alliance: 'NATO',
  icon: ICONS.HELICOPTER,

  capacity: {
    stretchers: 6,
    ambulatory: 16,
    crew: 3,
    medics: 1
  },

  performance: {
    cruise_speed_kmh: 257,
    max_speed_kmh: 294,
    range_km: 548,
    combat_radius_km: 230
  },

  medical: {
    pfc_capable: false,
    blood_refrigeration: false
  },

  survivability: {
    countermeasures: 'BASIC'
  },

  fielding_status: 'CURRENT',

  educational: {
    key_point: 'RAF SHF medium utility/MEDEVAC. NOT the same as Romanian IAR-330 Puma — different aircraft.',
    teaching_note: 'UK replaces Puma HC2 with AW149 from ~2025 onward. Chinook HC6A is preferred for MASCAL lift.',
    doctrinal_note: 'JHC (Joint Helicopter Command) tasking. Not army-organic — requires JHC coordination.'
  }
};

// ============================================================================
// WILDCAT AH1 / LYNX AH9A - British Army Air Corps
// Note: Primarily armed reconnaissance. Limited CASEVAC, NOT primary MEDEVAC platform.
// ============================================================================

export const WILDCAT_AH1 = {
  code: 'WILDCAT_AH1',
  name: 'Wildcat AH1 / Lynx AH9A',
  nato_designation: 'Wildcat',
  manufacturer: 'Leonardo (Westland)',
  nationality: 'British',
  alliance: 'NATO',
  icon: ICONS.HELICOPTER,

  capacity: {
    stretchers: 2,
    ambulatory: 4,
    crew: 2,
    medics: 0
  },

  performance: {
    cruise_speed_kmh: 259,
    max_speed_kmh: 291,
    range_km: 777,
    combat_radius_km: 300
  },

  medical: {
    pfc_capable: false,
    blood_refrigeration: false
  },

  survivability: {
    countermeasures: 'ADVANCED'
  },

  fielding_status: 'CURRENT',

  educational: {
    key_point: 'British AAC — armed recce primary role. Emergency CASEVAC capability only. 2-litter max.',
    teaching_note: 'Do NOT plan MEDEVAC sorties around Wildcat. AW101 Merlin or RAF Chinook for proper MEDEVAC.',
    doctrinal_note: 'JSP 950. AAC Wildcat is NOT a MEDEVAC aircraft — it is a combat aircraft with emergency CASEVAC as secondary capability.'
  }
};

// ============================================================================
// CIVILIAN HEMS - Generic civilian air ambulance (PERMISSIVE only)
// ============================================================================

export const CIVILIAN_HEMS = {
  code: 'CIVILIAN_HEMS',
  name: 'Civilian HEMS',
  nato_designation: 'N/A',
  manufacturer: 'Various (EC135, H145, AW169)',
  nationality: 'Civilian',
  alliance: 'CIVILIAN',
  icon: ICONS.HELICOPTER,

  capacity: {
    stretchers: 1,
    ambulatory: 2,
    crew: 2,
    medics: 1
  },

  performance: {
    cruise_speed_kmh: 220,
    max_speed_kmh: 260,
    range_km: 400,
    combat_radius_km: 0  // Not applicable - PERMISSIVE only
  },

  medical: {
    pfc_capable: false,
    blood_refrigeration: true,
    icu_capable: true  // Civilian HEMS often higher standard than military
  },

  survivability: {
    countermeasures: 'NONE'
  },

  fielding_status: 'CIVILIAN',

  educational: {
    key_point: 'PERMISSIVE environments ONLY. Civilian crew cannot be ordered into contested airspace.',
    teaching_note: 'Useful for rear-area evacuation and patient transfer in non-kinetic zones.',
    doctrinal_note: 'AJP-4.10C. Civilian crews operate under HEMS regulations not military command. Cannot be tasked into threat environment.'
  }
};

// ============================================================================
// HELICOPTER AGGREGATIONS
// ============================================================================

export const HELICOPTER_TYPES = {
  // Romanian
  IAR330:      IAR330_PUMA,
  // US
  UH60:        UH60_BLACK_HAWK,
  CH47:        CH47_CHINOOK,
  // German / European
  H145M:       H145M,
  NH90:        NH90,
  // British
  AW101:       AW101_MERLIN,
  PUMA_HC2:    PUMA_HC2,
  WILDCAT_AH1: WILDCAT_AH1,
  // French / Polish (H225M also used by Hungary, others)
  EC725:       EC725_CARACAL,
  // Polish legacy
  W3_SOKOL:    W3_SOKOL,
  // Legacy Soviet (partner nations)
  MI17:        MI17_HIP,
  // Civilian HEMS (permissive only)
  CIVILIAN_HEMS: CIVILIAN_HEMS
};

// Alias for backward compatibility (Tab 3 imports this name)
export const HELICOPTER_SPECS = HELICOPTER_TYPES;

export const HELICOPTER_CATEGORIES = {
  LIGHT:    ['H145M', 'WILDCAT_AH1', 'CIVILIAN_HEMS'],
  MEDIUM:   ['IAR330', 'UH60', 'NH90', 'EC725', 'W3_SOKOL', 'PUMA_HC2'],
  HEAVY:    ['CH47', 'AW101', 'MI17']
};

// Per-nation primary MEDEVAC aircraft.
// Used by Tab3 3B.0 to populate default air_pool aircraft_type when user selects a pool.
// Each nation lists: [primary_medevac, secondary_medevac (optional)]
export const HELICOPTER_BY_NATIONALITY = {
  ROMANIAN: ['IAR330'],
  US:       ['UH60', 'CH47'],
  GERMAN:   ['NH90', 'H145M'],        // NH90 = primary MEDEVAC; H145M = light utility secondary
  UK:       ['AW101', 'PUMA_HC2'],    // AW101 Merlin = primary MEDEVAC; Puma HC2 = RAF SHF secondary
  BRITISH:  ['AW101', 'PUMA_HC2'],    // Alias
  FRENCH:   ['NH90', 'EC725'],         // NH90 Caïman = primary ALAT MEDEVAC (63 delivered Feb 2025, replaced Puma); EC725 Caracal = SOF/CSAR only (4e RHFS)
  POLISH:   ['W3_SOKOL', 'EC725'],    // W-3 Sokol legacy primary; H225M/EC725 new acquisition
  EUROPEAN: ['NH90'],                 // Generic NATO European (Belgium, Netherlands, Norway, etc.)
  LEGACY_SOVIET: ['MI17'],
  CIVILIAN: ['CIVILIAN_HEMS']
};

export const HELICOPTER_BY_ROLE = {
  PRIMARY_MEDEVAC:    ['IAR330', 'UH60', 'H145M', 'NH90', 'EC725', 'AW101', 'W3_SOKOL', 'PUMA_HC2'],
  MASCAL:             ['CH47', 'AW101', 'MI17', 'NH90'],
  PFC_CAPABLE:        ['UH60', 'CH47', 'H145M', 'AW101', 'NH90', 'EC725'],
  CASEVAC_SECONDARY:  ['WILDCAT_AH1'],   // Armed recce with emergency CASEVAC only
  PERMISSIVE_ONLY:    ['CIVILIAN_HEMS'],
  NOT_MEDEVAC:        ['WILDCAT_AH1']    // Document platforms that should NOT be MEDEVAC-planned
};

// ============================================================================
// MEDEVAC OPERATIONAL FACTORS
// ============================================================================

export const MEDEVAC_OPERATIONAL_FACTORS = {
  // -------------------------------------------------------------------------
  // AVAILABILITY FACTORS
  // -------------------------------------------------------------------------
  maintenance_availability: 0.75,   // Proportion of fleet available after mx
  crew_availability:        0.85,   // Proportion of crews available for tasking
  weather_impact_average:   0.85,   // Average weather degradation across all ops
  contested_environment_modifier: 0.50, // Global modifier for contested airspace

  // -------------------------------------------------------------------------
  // MED-11: MEDEVAC planning speed factors (February 24, 2026)
  // PLANNING FACTORS, not cruise speeds. Account for cautious approach in
  // contested airspace, LZ orbit/assessment, non-direct routing, and combined
  // load+unload time. Distinct from per-aircraft cruise_speed_kmh.
  // Source: NATO AJMedP-2 MEDEVAC planning guidance; Ukraine 2022-2024 data.
  // -------------------------------------------------------------------------
  evac_planning_mins_per_km:   2,   // 2 min/km effective (30 km/h planning rate)
  evac_planning_load_minutes: 15,   // combined patient load + unload (LZ + MTF)

  // -------------------------------------------------------------------------
  // SORTIES PER AIRCRAFT PER DAY — by environment / AD threat level
  // Phase H (ARCH-014): medevacModule reads opFactors.sorties_per_aircraft_per_day?.[envKey]
  // envKey = AD threat code (PERMISSIVE / LOW / MEDIUM / HIGH / DENIED)
  //       OR environment string (PERMISSIVE / CONTESTED / DENIED)
  // Both key sets provided here to satisfy both call sites.
  //
  // Source: NATO AJMedP-2 MEDEVAC planning; JTAARS; Ukraine 2022-2024 operational data.
  // -------------------------------------------------------------------------
  sorties_per_aircraft_per_day: {
    // By AD threat level (consumed by medevacModule.js via envKey lookup)
    PERMISSIVE: 8,   // Full air superiority — standard planning figure
    LOW:        7,   // MANPADS threat only — minor route extensions required
    MEDIUM:     5,   // Mobile SAM (Strela/Osa) — NOE flight, 16% time penalty
    HIGH:       4,   // Integrated AD (Buk/Tor) — very constrained ops window
    DENIED:     3,   // Dense IADS — emergency only; maximum crew risk accepted
    // By environment string (consumed by calculateDailyMEDEVACCapacity)
    CONTESTED:  6    // Generic contested baseline; use AD keys above when known
  },

  // -------------------------------------------------------------------------
  // PATIENTS PER SORTIE — planning factor by helicopter code
  // NOT the aircraft's max stretcher capacity. Represents realistic planning
  // load: mixed T1/T2 triage, partial fills during high-tempo MASCAL, aircrew
  // weight margins for ballistic protection vests and crew medic equipment.
  // Typically 60–70% of declared stretcher capacity as planning factor.
  // Source: JTAARS rotary-wing planning tables; AJMedP-2 Annex B.
  // -------------------------------------------------------------------------
  patients_per_sortie: {
    IAR330:      4,   // 6 stretcher capacity → plan 4 (67%)
    UH60:        4,   // 6 stretcher capacity → plan 4 (67%)
    CH47:        16,  // 24 stretcher capacity → plan 16 (67%)
    H145M:       1,   // 2 stretcher capacity → plan 1 (50%, LZ weight limit)
    AW101:       10,  // 16 stretcher capacity → plan 10 (63%)
    NH90:        8,   // 12 stretcher capacity → plan 8 (67%)
    MI17:        8,   // 12 stretcher capacity → plan 8 (67%)
    EC725:       4,   // 6 stretcher capacity → plan 4 (67%) — same class as IAR330/UH60
    W3_SOKOL:    3,   // 4 stretcher capacity → plan 3 (75%)
    PUMA_HC2:    4,   // 6 stretcher capacity → plan 4 (67%) — RAF SHF medium
    WILDCAT_AH1: 1,   // Emergency CASEVAC only — 2 litter MAX, plan 1 (NOT a MEDEVAC aircraft)
    CIVILIAN_HEMS: 1, // Single-patient HEMS standard
    DEFAULT:     4    // Fallback when helicopter code unknown
  },

  // -------------------------------------------------------------------------
  // NTM (NOTICE TO MOVE) PER AIRCRAFT — minutes from alert to wheels-up
  // Source: PECC doctrine (confirmed in CLAUDE_READING_LIST, settled fact).
  // IAR330=10 / UH-60=15 / Bell412=15 / CH47=30 (PECC reference).
  // Values for H145M, AW101, NH90, MI17 estimated from similar class.
  // -------------------------------------------------------------------------
  ntm_minutes: {
    IAR330:      10,  // PECC confirmed
    UH60:        15,  // PECC confirmed
    CH47:        30,  // PECC confirmed — twin-rotor preflight checklist
    H145M:       15,  // Light twin — equivalent to Bell 412
    AW101:       20,  // Heavy — longer preflight than UH60
    NH90:        15,  // Medium twin — equivalent to UH60
    MI17:        20,  // Legacy heavy — estimated, limited NATO data
    EC725:       15,  // Medium twin — equivalent class to NH90/UH60
    W3_SOKOL:    15,  // Medium single — estimated from similar class
    PUMA_HC2:    15,  // Medium twin — RAF SHF operational NTM
    WILDCAT_AH1: 10,  // Light — faster preflight (armed recce design)
    CIVILIAN_HEMS: 5, // HEMS standard — maintained on standby, rapid response
    DEFAULT:     15   // NATO planning default when type unknown
  },

  // -------------------------------------------------------------------------
  // WEATHER DEGRADATION — sortie modifier by weather condition key
  // Mirrors WEATHER_MODIFIERS.sortie_modifier for direct lookup in medevacModule.
  // Keys must match WEATHER_MODIFIERS export keys exactly.
  // -------------------------------------------------------------------------
  weather_degradation: {
    VMC_CLEAR:    { sortie_modifier: 1.00, nvg_effective: true,  ifr_required: false },
    VMC_MARGINAL: { sortie_modifier: 0.85, nvg_effective: true,  ifr_required: false },
    IMC_LIGHT:    { sortie_modifier: 0.60, nvg_effective: false, ifr_required: true  },
    IMC_MODERATE: { sortie_modifier: 0.40, nvg_effective: false, ifr_required: true  },
    IMC_SEVERE:   { sortie_modifier: 0.15, nvg_effective: false, ifr_required: true  },
    GROUNDED:     { sortie_modifier: 0.00, nvg_effective: false, ifr_required: true  }
  },

  // -------------------------------------------------------------------------
  // AD THREAT MODIFIER — flat sortie modifier by AD threat code
  // Mirrors AD_THREAT_MODIFIERS.sortie_modifier for direct lookup in medevacModule.
  // Keys must match AD_THREAT_MODIFIERS export keys exactly.
  // -------------------------------------------------------------------------
  ad_threat_modifier: {
    PERMISSIVE: 1.00,
    LOW:        0.85,
    MEDIUM:     0.60,
    HIGH:       0.30,
    DENIED:     0.00
  },

  // -------------------------------------------------------------------------
  // ALERT POSTURES
  // -------------------------------------------------------------------------
  alert_postures: {
    ALERT_5:    { minutes: 5,  description: 'Crew at aircraft',      icon: ICONS.GREEN },
    ALERT_15:   { minutes: 15, description: 'Crew in crew room',     icon: ICONS.YELLOW },
    ALERT_30:   { minutes: 30, description: 'Crew on base',          icon: ICONS.ORANGE },
    ALERT_60:   { minutes: 60, description: 'Crew on call',          icon: ICONS.RED },
    COLD_START: { minutes: 90, description: 'Cold aircraft startup', icon: ICONS.RED }
  }
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get helicopter options for UI dropdown
 * @param {Object} filters - Optional filters
 * @returns {Array} Helicopter options
 */
export const getHelicopterOptions = (filters = {}) => {
  let helicopters = Object.entries(HELICOPTER_TYPES);
  
  if (filters.category) {
    const codes = HELICOPTER_CATEGORIES[filters.category] || [];
    helicopters = helicopters.filter(([code]) => codes.includes(code));
  }
  
  if (filters.role) {
    const codes = HELICOPTER_BY_ROLE[filters.role] || [];
    helicopters = helicopters.filter(([code]) => codes.includes(code));
  }
  
  if (filters.excludeLegacy) {
    helicopters = helicopters.filter(([, spec]) => spec.fielding_status !== 'LEGACY');
  }
  
  if (filters.pfcCapable) {
    helicopters = helicopters.filter(([, spec]) => spec.medical.pfc_capable);
  }
  
  return helicopters.map(([code, spec]) => ({
    value: code,
    label: `${spec.flag} ${spec.name}`,
    icon: spec.icon || ICONS.HELICOPTER,
    capacity: spec.capacity.stretchers,
    speed: spec.performance.cruise_speed_kmh,
    range: spec.performance.range_km,
    pfc_capable: spec.medical.pfc_capable,
    countermeasures: spec.survivability.countermeasures,
    fielding_status: spec.fielding_status,
    nationality: spec.nationality
  }));
};

/**
 * Get drone threat options for UI dropdown
 * @returns {Array} Drone threat options
 */
export const getDroneThreatOptions = () => {
  return Object.entries(DRONE_THREAT_MODIFIERS).map(([key, mod]) => ({
    value: key,
    label: mod.description,
    icon: mod.icon,
    color: mod.color,
    sortieModifier: mod.sortie_modifier,
    capacityReduction: Math.round((1 - mod.sortie_modifier) * 100) + '%'
  }));
};

/**
 * Get AD threat options for UI dropdown
 * @returns {Array} AD threat options
 */
export const getADThreatOptions = () => {
  return Object.entries(AD_THREAT_MODIFIERS).map(([key, mod]) => ({
    value: key,
    label: mod.description,
    icon: mod.icon,
    color: mod.color,
    sortieModifier: mod.sortie_modifier,
    flightProfile: mod.flight_profile
  }));
};

/**
 * Get weather options for UI dropdown
 * @returns {Array} Weather options
 */
export const getWeatherOptions = () => {
  return Object.entries(WEATHER_MODIFIERS).map(([key, mod]) => ({
    value: key,
    label: mod.description,
    icon: mod.icon,
    color: mod.color,
    sortieModifier: mod.sortie_modifier,
    visibility: mod.visibility_km
  }));
};

/**
 * Get terrain options for UI dropdown
 * @returns {Array} Terrain options
 */
export const getTerrainOptions = () => {
  return Object.entries(TERRAIN_MODIFIERS).map(([key, mod]) => ({
    value: key,
    label: mod.description,
    icon: mod.icon,
    sortieModifier: mod.sortie_modifier,
    lzAvailability: mod.lz_availability
  }));
};

/**
 * Get altitude options for UI dropdown
 * @returns {Array} Altitude options
 */
export const getAltitudeOptions = () => {
  return Object.entries(ALTITUDE_PERFORMANCE).map(([key, perf]) => ({
    value: key,
    label: perf.altitude_range,
    icon: perf.icon,
    payloadModifier: perf.payload_modifier
  }));
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get helicopter specifications by code
 * @param {string} code - Helicopter code
 * @returns {Object|null} Helicopter specification
 */
// Helicopter names used by Tab 3 tactical evacuation assets and older saves, mapped to the HELICOPTER_TYPES keys.
export const HELICOPTER_TYPE_ALIASES = {
  IAR330_PUMA: 'IAR330',
  UH60_BLACKHAWK: 'UH60',
  CH47_CHINOOK: 'CH47'
};

export const getHelicopterSpecs = (code) => {
  const upperCode = code?.toUpperCase();
  return HELICOPTER_TYPES[upperCode] || HELICOPTER_TYPES[HELICOPTER_TYPE_ALIASES[upperCode]] || null;
};

/**
 * Get all helicopter codes
 * @returns {Array} Array of helicopter codes
 */
export const getHelicopterCodes = () => {
  return Object.keys(HELICOPTER_TYPES);
};

/**
 * Get helicopters by nationality
 * @param {string} nationality - Nationality code
 * @returns {Array} Array of helicopter specs
 */
export const getHelicoptersByNationality = (nationality) => {
  const codes = HELICOPTER_BY_NATIONALITY[nationality] || [];
  return codes.map(code => HELICOPTER_TYPES[code]).filter(Boolean);
};

/**
 * Get helicopters by role
 * @param {string} role - Role code
 * @returns {Array} Array of helicopter specs
 */
export const getHelicoptersByRole = (role) => {
  const codes = HELICOPTER_BY_ROLE[role] || [];
  return codes.map(code => HELICOPTER_TYPES[code]).filter(Boolean);
};

/**
 * Get helicopters by category
 * @param {string} category - Category code
 * @returns {Array} Array of helicopter specs
 */
export const getHelicoptersByCategory = (category) => {
  const codes = HELICOPTER_CATEGORIES[category] || [];
  return codes.map(code => HELICOPTER_TYPES[code]).filter(Boolean);
};

/**
 * Get primary helicopter for national standard
 * @param {string} nationalStandard - National standard
 * @returns {Object} Primary helicopter spec
 */
export const getPrimaryHelicopter = (nationalStandard) => {
  switch (nationalStandard?.toUpperCase()) {
    case 'ROMANIAN':
      return IAR330_PUMA;
    case 'NATO':
    case 'US':
      return UH60_BLACK_HAWK;
    case 'GERMAN':
      return H145M;
    case 'UK':
      return AW101_MERLIN;
    case 'EUROPEAN':
      return NH90;
    default:
      return UH60_BLACK_HAWK;
  }
};

/**
 * Calculate basic evacuation time
 * @param {string} helicopterCode - Helicopter code
 * @param {number} distanceKm - One-way distance in km
 * @param {number} loadingMinutes - Loading time
 * @param {number} unloadingMinutes - Unloading time
 * @returns {Object} Evacuation time calculation
 */
export const calculateEvacuationTime = (helicopterCode, distanceKm, loadingMinutes = 5, unloadingMinutes = 3) => {
  const specs = getHelicopterSpecs(helicopterCode);
  if (!specs) return null;
  
  const cruiseSpeed = specs.performance.cruise_speed_kmh;
  const flightTimeMinutes = (distanceKm / cruiseSpeed) * 60;
  const totalMinutes = flightTimeMinutes + loadingMinutes + unloadingMinutes;
  
  const goldenHourAchievable = totalMinutes <= 60;
  
  return {
    helicopter: specs.name,
    helicopter_code: specs.code,
    distance_km: distanceKm,
    flight_time_minutes: Math.round(flightTimeMinutes),
    loading_time_minutes: loadingMinutes,
    unloading_time_minutes: unloadingMinutes,
    total_time_minutes: Math.round(totalMinutes),
    golden_hour_achievable: goldenHourAchievable,
    icon: goldenHourAchievable ? ICONS.GREEN : ICONS.RED
  };
};

/**
 * Calculate daily MEDEVAC capacity with all operational factors
 * 
 * This is the primary function for calculating helicopter MEDEVAC capacity.
 * It considers aircraft type, count, threat environment, weather, and terrain.
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.helicopterCode - Helicopter type code (IAR330, UH60, etc.)
 * @param {number} config.aircraftCount - Number of available aircraft
 * @param {string} config.environment - PERMISSIVE, CONTESTED, or DENIED
 * @param {string} config.adThreat - AD threat level
 * @param {string} config.weather - Weather conditions
 * @param {string} config.terrain - Terrain type
 * @param {number} config.avgDistanceKm - Average evacuation distance
 * @returns {Object} Daily capacity analysis
 * 
 * USAGE:
 * const capacity = calculateDailyMEDEVACCapacity({
 *   helicopterCode: 'UH60',
 *   aircraftCount: 4,
 *   environment: 'CONTESTED',
 *   adThreat: 'MEDIUM',
 *   weather: 'VMC_CLEAR',
 *   terrain: 'ROLLING',
 *   avgDistanceKm: 50
 * });
 */
export const calculateDailyMEDEVACCapacity = (config) => {
  const {
    helicopterCode,
    aircraftCount = 1,
    environment = 'CONTESTED',
    adThreat = 'LOW',
    droneThreat = 'MEDIUM',
    weather = 'VMC_CLEAR',
    terrain = 'ROLLING',
    avgDistanceKm = 50,
    nightOps = false
  } = config;
  
  const specs = getHelicopterSpecs(helicopterCode);
  if (!specs) {
    return {
      error: `Unknown helicopter code: ${helicopterCode}`,
      valid: false,
      daily_capacity: 0,
      icon: ICONS.RED
    };
  }
  
  // Get operational factors
  const opFactors = MEDEVAC_OPERATIONAL_FACTORS;
  
  // Determine sorties per day from constant — environment key takes precedence,
  // falls back to AD threat key, then CONTESTED default.
  // Keys in sorties_per_aircraft_per_day: PERMISSIVE, LOW, MEDIUM, HIGH, DENIED, CONTESTED.
  const sortiesTable = opFactors.sorties_per_aircraft_per_day;
  const baseSortiesPerDay =
    sortiesTable?.[environment] ??
    sortiesTable?.[adThreat]   ??
    sortiesTable?.CONTESTED    ??
    6; // hard-coded last-resort only — should never fire with v2.3+ constant
  
  // Get modifiers
  const adMod = AD_THREAT_MODIFIERS[adThreat] || AD_THREAT_MODIFIERS.LOW;
  const droneMod = DRONE_THREAT_MODIFIERS[droneThreat] || DRONE_THREAT_MODIFIERS.MEDIUM;
  const weatherMod = WEATHER_MODIFIERS[weather] || WEATHER_MODIFIERS.VMC_CLEAR;
  const terrainMod = TERRAIN_MODIFIERS[terrain] || TERRAIN_MODIFIERS.ROLLING;
  
  // Night operations modifier
  let nightMod = 1.0;
  if (nightOps) {
    if (specs.operations.flir_equipped) {
      nightMod = 0.90;
    } else if (specs.operations.nvg_compatible) {
      nightMod = 0.85;
    } else {
      nightMod = 0.30;
    }
  }
  
  // Maintenance availability
  const maintAvail = opFactors.maintenance_availability || 0.75;
  const crewAvail = opFactors.crew_availability || 0.85;
  
  // Combined threat modifier
  const threatModifier = adMod.sortie_modifier * droneMod.sortie_modifier;
  
  // Combined environmental modifier
  const envModifier = weatherMod.sortie_modifier * terrainMod.sortie_modifier * nightMod;
  
  // Combined availability modifier
  const availModifier = maintAvail * crewAvail;
  
  // Overall modifier
  const overallModifier = threatModifier * envModifier * availModifier;
  
  // Calculate sorties
  const effectiveSorties = baseSortiesPerDay * overallModifier;
  
  // Patient capacity per sortie
  const patientsPerSortie = specs.capacity.stretchers;
  
  // Calculate daily capacity
  const dailyCapacityPerAircraft = Math.floor(effectiveSorties * patientsPerSortie);
  const totalDailyCapacity = dailyCapacityPerAircraft * aircraftCount;
  
  // Calculate time per sortie
  const avgSpeed = specs.performance.cruise_speed_kmh || 240;
  const flightTimeMinutes = (avgDistanceKm * 2 / avgSpeed) * 60;
  const groundTimeMinutes = 30; // Loading + unloading
  const sortieTimeMinutes = flightTimeMinutes + groundTimeMinutes;
  
  // Golden hour analysis
  const oneWayTimeMinutes = (avgDistanceKm / avgSpeed) * 60;
  const goldenHourAchievable = oneWayTimeMinutes <= 50; // Allow 10 min for loading
  
  // Generate warnings
  const warnings = [];
  
  if (adMod.code === 'DENIED') {
    warnings.push({
      severity: 'CRITICAL',
      code: 'AD_DENIED',
      message: 'Helicopter operations denied by air defense threat',
      icon: ICONS.RED
    });
  }
  
  if (threatModifier < 0.3) {
    warnings.push({
      severity: 'WARNING',
      code: 'HIGH_THREAT',
      message: 'Combined threat reduces capacity by >70%',
      icon: ICONS.ORANGE
    });
  }
  
  if (!goldenHourAchievable) {
    warnings.push({
      severity: 'WARNING',
      code: 'GOLDEN_HOUR',
      message: `Distance (${avgDistanceKm}km) exceeds golden hour range`,
      icon: ICONS.YELLOW
    });
  }
  
  if (specs.fielding_status === 'LEGACY') {
    warnings.push({
      severity: 'INFO',
      code: 'LEGACY_AIRCRAFT',
      message: `${specs.name} is LEGACY status - limited survivability`,
      icon: ICONS.YELLOW
    });
  }
  
  // Determine overall status
  let status, statusIcon;
  if (totalDailyCapacity === 0 || warnings.some(w => w.severity === 'CRITICAL')) {
    status = 'DENIED';
    statusIcon = ICONS.RED;
  } else if (overallModifier < 0.4) {
    status = 'SEVERELY_DEGRADED';
    statusIcon = ICONS.ORANGE;
  } else if (overallModifier < 0.7) {
    status = 'DEGRADED';
    statusIcon = ICONS.YELLOW;
  } else {
    status = 'OPERATIONAL';
    statusIcon = ICONS.GREEN;
  }
  
  return {
    valid: true,
    
    // Helicopter info
    helicopter_code: helicopterCode,
    helicopter_name: specs.name,
    aircraft_count: aircraftCount,
    
    // Base data
    base_sorties_per_day: baseSortiesPerDay,
    patients_per_sortie: patientsPerSortie,
    
    // Modifiers
    modifiers: {
      threat: Math.round(threatModifier * 100) / 100,
      environment: Math.round(envModifier * 100) / 100,
      availability: Math.round(availModifier * 100) / 100,
      overall: Math.round(overallModifier * 100) / 100
    },
    
    // Capacity calculations
    effective_sorties_per_aircraft: Math.round(effectiveSorties * 10) / 10,
    daily_capacity_per_aircraft: dailyCapacityPerAircraft,
    total_daily_capacity: totalDailyCapacity,
    
    // Capacity reduction
    capacity_reduction_percent: Math.round((1 - overallModifier) * 100),
    
    // Time analysis
    avg_sortie_time_minutes: Math.round(sortieTimeMinutes),
    avg_flight_time_minutes: Math.round(flightTimeMinutes),
    max_sorties_per_24h: Math.floor((24 * 60) / sortieTimeMinutes),
    
    // Golden hour
    golden_hour_achievable: goldenHourAchievable,
    one_way_time_minutes: Math.round(oneWayTimeMinutes),
    
    // Status
    status,
    warnings,
    warning_count: warnings.length,
    has_critical_warning: warnings.some(w => w.severity === 'CRITICAL'),
    icon: statusIcon,
    
    // Educational
    educational: {
      key_point: `${aircraftCount} ${specs.name} can evacuate ${totalDailyCapacity} patients/day in ${environment} environment`,
      teaching_note: overallModifier < 0.5 
        ? 'Consider ground evacuation as primary mode'
        : 'Helicopter MEDEVAC viable but monitor threat changes'
    }
  };
};

/**
 * Calculate MEDEVAC capacity with all modifiers
 * @param {string} helicopterCode - Helicopter code
 * @param {number} aircraftCount - Number of aircraft
 * @param {Object} environment - Environmental factors
 * @returns {Object} Capacity calculation with warnings
 */
export const calculateMEDEVACCapacityEnhanced = (
  helicopterCode,
  aircraftCount,
  environment = {}
) => {
  const specs = getHelicopterSpecs(helicopterCode);
  if (!specs) return null;
  
  const {
    adThreat = 'LOW',
    droneThreat = 'MEDIUM',
    weather = 'VMC_CLEAR',
    terrain = 'ROLLING',
    nightOps = false,
    operatingAltitudeM = 500
  } = environment;
  
  // Get modifiers
  const adMod = AD_THREAT_MODIFIERS[adThreat] || AD_THREAT_MODIFIERS.LOW;
  const droneMod = DRONE_THREAT_MODIFIERS[droneThreat] || DRONE_THREAT_MODIFIERS.MEDIUM;
  const weatherMod = WEATHER_MODIFIERS[weather] || WEATHER_MODIFIERS.VMC_CLEAR;
  const terrainMod = TERRAIN_MODIFIERS[terrain] || TERRAIN_MODIFIERS.ROLLING;
  
  // Night modifier
  let nightMod = 1.0;
  if (nightOps) {
    if (specs.operations.flir_equipped) {
      nightMod = NIGHT_OPERATIONS_MODIFIERS.FLIR_EQUIPPED.sortie_modifier;
    } else if (specs.operations.nvg_compatible) {
      nightMod = NIGHT_OPERATIONS_MODIFIERS.NVG_EQUIPPED.sortie_modifier;
    } else {
      nightMod = NIGHT_OPERATIONS_MODIFIERS.NON_NVG.sortie_modifier;
    }
  }
  
  // Altitude modifier
  let altitudeMod = 1.0;
  if (operatingAltitudeM > 3500) {
    altitudeMod = ALTITUDE_PERFORMANCE.VERY_HIGH_ALTITUDE.payload_modifier;
  } else if (operatingAltitudeM > 2500) {
    altitudeMod = ALTITUDE_PERFORMANCE.HIGH_ALTITUDE.payload_modifier;
  } else if (operatingAltitudeM > 1500) {
    altitudeMod = ALTITUDE_PERFORMANCE.MEDIUM_ALTITUDE.payload_modifier;
  }
  
  // Base calculations — read sorties from constant, not hardcoded
  const baseSorties =
    requireNumber(MEDEVAC_OPERATIONAL_FACTORS.sorties_per_aircraft_per_day?.CONTESTED, 'helicopterSpecs.js:1970', 'MEDEVAC_OPERATIONAL_FACTORS.sorties_per_aircraft_per_day.CONTESTED');
  const patientsPerSortie = specs.capacity.stretchers;
  const maintAvail = MEDEVAC_OPERATIONAL_FACTORS.maintenance_availability;
  const crewAvail = MEDEVAC_OPERATIONAL_FACTORS.crew_availability;
  
  // Combined modifier
  const combinedMod = adMod.sortie_modifier * droneMod.sortie_modifier * 
                      weatherMod.sortie_modifier * terrainMod.sortie_modifier *
                      nightMod * altitudeMod;
  
  // Calculate capacity
  const rawCapacity = aircraftCount * baseSorties * patientsPerSortie;
  const availableCapacity = rawCapacity * maintAvail * crewAvail;
  const effectiveCapacity = availableCapacity * combinedMod;
  
  // Generate warnings
  const warnings = [];
  
  if (adMod.code === 'DENIED') {
    warnings.push({
      severity: 'CRITICAL',
      code: 'AD_DENIED',
      message: 'Air defense threat denies helicopter operations. Use ground evacuation.',
      icon: ICONS.RED
    });
  }
  
  if (droneMod.code === 'EXTREME' || droneMod.code === 'HIGH') {
    warnings.push({
      severity: 'WARNING',
      code: 'DRONE_THREAT',
      message: `${droneMod.code} drone threat. ${droneMod.recommended_tactics}`,
      icon: ICONS.ORANGE
    });
  }
  
  if (specs.fielding_status === 'LEGACY') {
    warnings.push({
      severity: 'INFO',
      code: 'LEGACY_AIRCRAFT',
      message: `${specs.name} is LEGACY status with limited survivability.`,
      icon: ICONS.YELLOW
    });
  }
  
  // Determine overall status icon
  let statusIcon = ICONS.GREEN;
  if (warnings.some(w => w.severity === 'CRITICAL')) {
    statusIcon = ICONS.RED;
  } else if (warnings.some(w => w.severity === 'WARNING')) {
    statusIcon = ICONS.ORANGE;
  } else if (warnings.length > 0) {
    statusIcon = ICONS.YELLOW;
  }
  
  return {
    helicopter: specs.name,
    helicopter_code: specs.code,
    aircraft_count: aircraftCount,
    
    raw_daily_capacity: Math.round(rawCapacity),
    available_capacity: Math.round(availableCapacity),
    effective_capacity: Math.round(effectiveCapacity),
    capacity_reduction_percent: Math.round((1 - effectiveCapacity / rawCapacity) * 100),
    
    combined_modifier: Math.round(combinedMod * 100) / 100,
    
    warnings,
    warning_count: warnings.length,
    has_critical_warning: warnings.some(w => w.severity === 'CRITICAL'),
    operations_viable: effectiveCapacity > 0 && !warnings.some(w => w.severity === 'CRITICAL'),
    
    icon: statusIcon
  };
};

/**
 * Get MEDEVAC recommendation for threat level
 * @param {string} adThreat - AD threat level
 * @param {string} droneThreat - Drone threat level
 * @param {string} weather - Weather conditions
 * @returns {Object} Recommendation
 */
export const getMEDEVACRecommendation = (adThreat, droneThreat, weather) => {
  const adMod = AD_THREAT_MODIFIERS[adThreat] || AD_THREAT_MODIFIERS.LOW;
  const droneMod = DRONE_THREAT_MODIFIERS[droneThreat] || DRONE_THREAT_MODIFIERS.MEDIUM;
  const weatherMod = WEATHER_MODIFIERS[weather] || WEATHER_MODIFIERS.VMC_CLEAR;
  
  const combinedMod = adMod.sortie_modifier * droneMod.sortie_modifier * weatherMod.sortie_modifier;
  
  let recommendation, alertPosture, primaryMode, icon;
  
  if (combinedMod >= 0.7) {
    recommendation = 'FULL_OPERATIONS';
    alertPosture = 'ALERT_15';
    primaryMode = 'AIR';
    icon = ICONS.GREEN;
  } else if (combinedMod >= 0.4) {
    recommendation = 'REDUCED_OPERATIONS';
    alertPosture = 'ALERT_30';
    primaryMode = 'AIR_PREFERRED';
    icon = ICONS.YELLOW;
  } else if (combinedMod >= 0.2) {
    recommendation = 'EMERGENCY_ONLY';
    alertPosture = 'ALERT_60';
    primaryMode = 'GROUND_PREFERRED';
    icon = ICONS.ORANGE;
  } else {
    recommendation = 'GROUND_EVACUATION_ONLY';
    alertPosture = null;
    primaryMode = 'GROUND_ONLY';
    icon = ICONS.RED;
  }
  
  return {
    recommendation,
    alert_posture: alertPosture,
    primary_mode: primaryMode,
    combined_modifier: Math.round(combinedMod * 100) / 100,
    helicopter_operations_viable: combinedMod > 0.1,
    icon
  };
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate helicopter configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateHelicopterConfig = (config) => {
  const errors = [];
  const warnings = [];
  
  // Validate helicopter code
  if (config.helicopterCode && !getHelicopterSpecs(config.helicopterCode)) {
    errors.push({
      field: 'helicopterCode',
      message: `Invalid helicopter code: ${config.helicopterCode}`,
      icon: ICONS.RED
    });
  }
  
  // Validate aircraft count
  if (config.aircraftCount !== undefined) {
    if (config.aircraftCount < 0) {
      errors.push({
        field: 'aircraftCount',
        message: 'Aircraft count cannot be negative',
        icon: ICONS.RED
      });
    }
    if (config.aircraftCount > 100) {
      warnings.push({
        field: 'aircraftCount',
        message: 'Aircraft count seems unusually high',
        icon: ICONS.YELLOW
      });
    }
  }
  
  // Validate threat levels
  if (config.adThreat && !(config.adThreat in AD_THREAT_MODIFIERS)) {
    errors.push({
      field: 'adThreat',
      message: `Invalid AD threat level: ${config.adThreat}`,
      icon: ICONS.RED
    });
  }
  
  if (config.droneThreat && !(config.droneThreat in DRONE_THREAT_MODIFIERS)) {
    errors.push({
      field: 'droneThreat',
      message: `Invalid drone threat level: ${config.droneThreat}`,
      icon: ICONS.RED
    });
  }
  
  // Check for inconsistent threat configurations
  if (config.adThreat === 'PERMISSIVE' && config.droneThreat === 'EXTREME') {
    warnings.push({
      field: 'threat',
      message: 'Inconsistent threat: PERMISSIVE AD with EXTREME drone threat is unusual',
      icon: ICONS.WARNING
    });
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

/**
 * Check if helicopter code is valid
 * @param {string} code - Helicopter code
 * @returns {boolean} True if valid
 */
export const isValidHelicopterCode = (code) => {
  return code?.toUpperCase() in HELICOPTER_TYPES;
};

/**
 * Get all valid helicopter codes
 * @returns {Array} Valid codes
 */
export const getValidHelicopterCodes = () => {
  return Object.keys(HELICOPTER_TYPES);
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const helicopterSpecsConstants = {
  // Individual helicopter specs
  IAR330_PUMA,
  UH60_BLACK_HAWK,
  CH47_CHINOOK,
  H145M,
  AW101_MERLIN,
  NH90,
  MI17_HIP,
  // Phase 5.1 additions
  EC725_CARACAL,
  W3_SOKOL,
  PUMA_HC2,
  WILDCAT_AH1,
  CIVILIAN_HEMS,
  
  // Aggregated
  HELICOPTER_TYPES,
  HELICOPTER_SPECS,  // Alias for backward compatibility
  HELICOPTER_CATEGORIES,
  HELICOPTER_BY_NATIONALITY,
  HELICOPTER_BY_ROLE,
  
  // Modifiers
  DRONE_THREAT_MODIFIERS,
  AD_THREAT_MODIFIERS,
  WEATHER_MODIFIERS,
  TERRAIN_MODIFIERS,
  NIGHT_OPERATIONS_MODIFIERS,
  ALTITUDE_PERFORMANCE,
  COUNTERMEASURES,
  FIELDING_STATUS,
  
  // Operational factors
  MEDEVAC_OPERATIONAL_FACTORS,
  
  // UI Options
  getHelicopterOptions,
  getDroneThreatOptions,
  getADThreatOptions,
  getWeatherOptions,
  getTerrainOptions,
  getAltitudeOptions,
  
  // Helper functions
  getHelicopterSpecs,
  getHelicopterCodes,
  getHelicoptersByNationality,
  getHelicoptersByRole,
  getHelicoptersByCategory,
  getPrimaryHelicopter,
  calculateEvacuationTime,
  calculateDailyMEDEVACCapacity,
  calculateMEDEVACCapacityEnhanced,
  getMEDEVACRecommendation,
  
  // Validation
  validateHelicopterConfig,
  isValidHelicopterCode,
  getValidHelicopterCodes
};

export default helicopterSpecsConstants;
